import * as IkeApi from '@ikegps/core-api';
import { IkeRequestHeader } from '@ikegps/core-api';
import { IkeConfig, type IkeConfigKey, IkeConfigStore } from '../config';
import { Log } from '../log';
import { isJWT } from '../util';
import { IkeFetchableStore } from './fetchable';
import { IkeRequestError, IkeRequestErrorChecker, IkeRequestErrorType } from './rest.error';
import { IkeRestUser } from './rest.user';

export const REQUIRED_KEYS: IkeConfigKey[] = ['application.name', 'application.url', 'application.version'];

const HEADER_CONTENT_JSON = 'application/json';

export type ErrorListener = (error: IkeRequestError) => any;

export class IkeRest {
	static FetchableStore: typeof IkeFetchableStore = IkeFetchableStore;

	appName: string;
	baseUrl: string;
	version: string;

	fetch: typeof globalThis.fetch;
	log: Log;

	requester: string;
	rayId?: string;

	errorListeners: { [message: string]: ErrorListener[] } = {};

	Log = {
		create: this.bindAction<IkeApi.IkeActionLog>('log.create'),
	};

	Capture = {
		thumbnail: this.bindAction<IkeApi.IkeActionCaptureThumbnail>('capture.thumbnail'),
		composite: this.bindAction<IkeApi.IkeActionCaptureComposite>('collection.capture.composite'),
		measurement: this.bindAction<IkeApi.IkeActionCaptureMeasurement>('collection.capture.measurement'),
		brightness: this.bindAction<IkeApi.IkeActionCaptureBrightness>('collection.capture.brightness'),
		crop: this.bindAction<IkeApi.IkeActionCaptureCrop>('collection.capture.crop'),
	};

	SSOUser = {
		create: this.bindAction<IkeApi.IkeActionSSOUserCreate>('sso.user.create'),
		triggerResetPassword: this.bindAction<IkeApi.IkeActionSSOResetPassword>('sso.reset.password'),
		selfMigrate: this.bindAction<IkeApi.IkeActionSSOUserMigrate>('sso.user.migrate'),
	};

	Version = {
		check: this.bindAction<IkeApi.IkeActionVersionCheck>('version.check'),
		data: this.bindAction<IkeApi.IkeActionVersionFileGet>('version.data'),
	};

	constructor(
		log: Log,
		applicationName: string,
		baseUrl: string,
		applicationVersion: string,
		rayId?: string,
		fetch?: typeof globalThis.fetch
	) {
		this.appName = applicationName;
		this.baseUrl = baseUrl;
		this.version = applicationVersion;

		// Prefer an explicitly provided transport; fall back to the global for callers that don't pass one.
		this.fetch = fetch ?? IkeConfigStore.fetch;

		this.log = log;

		this.requester = `${this.appName}-${this.version}`;
		this.rayId = rayId;
	}

	static create(
		config: IkeConfig,
		opts: {
			log: Log;
			rayId?: string;
			fetch?: typeof globalThis.fetch;
		}
	) {
		return new IkeRest(
			opts.log,
			config.mustGetString('application.name'),
			config.getString('application.url.internal') || config.mustGetString('application.url'),
			config.mustGetString('application.version'),
			opts.rayId,
			opts.fetch
		);
	}

	static createStore<T>(): IkeFetchableStore<T> {
		return new IkeRest.FetchableStore<T>();
	}

	bind(token: string) {
		return new IkeRestUser(this, token);
	}

	bindAction<T extends IkeApi.IkeAction>(action: T['action']): (data?: T['request']) => Promise<T['response']> {
		return (data) => this.action<T>(action, data);
	}

	async authenticate(sso_id: string, token: string): Promise<IkeRestUser> {
		const data = { sso_id, token };
		const profile = await this.action<IkeApi.IkeActionAuthenticate>('authenticate', data, token);
		const userRest = this.bind(token);

		userRest.me.init(profile);

		return userRest;
	}

	onError(type: IkeRequestErrorType, callback: ErrorListener) {
		if (this.errorListeners[type] == null) {
			this.errorListeners[type] = [callback];
		} else {
			this.errorListeners[type].push(callback);
		}
	}

	action<T extends IkeApi.IkeAction>(
		path: T['action'],
		data: T['request'],
		sessionToken = null,
		departmentId = null
	): Promise<T['response']> {
		return this._post<T['request'], T['response'], T['action']>(path, data, sessionToken, departmentId);
	}

	actionRaw<T extends IkeApi.IkeAction>(
		path: T['action'],
		data: T['request'],
		sessionToken = null,
		departmentId = null
	) {
		return this._raw<T['request'], T['response'], T['action']>(path, data, sessionToken, departmentId);
	}

	headers(sessionToken: string, departmentId: string) {
		const headers = {};
		headers[IkeRequestHeader.REQUESTER] = this.requester;
		headers[IkeRequestHeader.CONTENT_TYPE] = HEADER_CONTENT_JSON;

		if (sessionToken != null) {
			// TODO: Remove JWT check and bearer prefix check by ensuring sessionToken is correct elsewhere
			if (isJWT(sessionToken)) {
				// If this already has the bearer prefix, don't append it again
				let authHeader = /^Bearer .*/.test(sessionToken) ? sessionToken : `Bearer ${sessionToken}`;

				headers[IkeRequestHeader.AUTHORIZATION] = authHeader;
			} else {
				headers[IkeRequestHeader.AUTHORIZATION] = `token ${sessionToken}`;
			}
		}

		if (this.rayId) {
			headers[IkeRequestHeader.RAY_ID] = this.rayId;
		}

		if (departmentId) {
			headers[IkeRequestHeader.DEPARTMENT] = departmentId;
		}

		return headers;
	}

	path(path: string) {
		if (path.indexOf('/') === -1) {
			return this.baseUrl + '/v1/action/' + path;
		}
		return this.baseUrl + path;
	}

	async _raw<Req, _Res, Path extends string>(path: Path, data?: Req, sessionToken?: string, departmentId?: string) {
		const body = data || {};
		const method = 'POST'; // IF this ever changes, need to remove BODY from request.

		const headers = this.headers(sessionToken, departmentId);
		const apiUrl = this.path(path);

		const startTime = Date.now();

		this.log.trace({ baseUrl: this.baseUrl }, `CallStart - ${path}`);

		const response = await this.fetch(apiUrl, { method, headers, body: JSON.stringify(body) } as any);

		const duration = Date.now() - startTime;
		const responseId = response.headers.get(IkeRequestHeader.REQUEST_ID);

		if (response.ok) {
			this.log.info({ responseId, duration, status: response.status }, `CallDone - ${path}`);
		} else {
			this.log.warn({ responseId, duration, status: response.status }, `CallDone - ${path}`);
		}

		return response;
	}

	async handleError(response: Response): Promise<null> {
		await this.validateIsJson(response);
		const json = await response.json();

		const requestError = new IkeRequestError(response.status, json);
		const requestErrorType = IkeRequestErrorChecker.getErrorType(requestError);
		const errorListeners = this.errorListeners[requestErrorType] || [];
		for (const listener of errorListeners) {
			listener(requestError);
		}

		throw new IkeRequestError(response.status, json);
	}

	async validateIsJson(response: Response): Promise<void> {
		const contentType = response.headers.get(IkeRequestHeader.CONTENT_TYPE) || '';
		if (contentType.indexOf(HEADER_CONTENT_JSON) === -1) {
			const text = await response.text();
			throw new IkeRequestError(response.status, { message: text });
		}
	}

	async _post<Req, Res, Path extends string>(
		path: Path,
		data?: Req,
		sessionToken?: string,
		departmentId?: string
	): Promise<Res> {
		const response = await this._raw(path, data, sessionToken, departmentId);

		if (response.ok) {
			await this.validateIsJson(response);
			const res = await response.json();

			return res;
		}

		return this.handleError(response);
	}
}
